import type { AnyRouter } from "@tanstack/react-router";

/** A second back press within this time leaves the app. */
const EXIT_WINDOW_MS = 2_000;

declare global {
  interface Window {
    /**
     * Called by the Android app when the phone's back button is pressed. Returns true when the
     * app handled it, or "exit" when the person pressed back twice on the home screen.
     */
    zeetaBack?: () => true | "exit";
  }
}

function toast(text: string) {
  const el = document.createElement("div");
  el.textContent = text;
  el.setAttribute("role", "status");
  el.style.cssText =
    "position:fixed;left:50%;bottom:calc(5rem + env(safe-area-inset-bottom));transform:translateX(-50%);" +
    "z-index:2147483647;max-width:calc(100% - 2rem);padding:.6rem 1rem;border-radius:9999px;" +
    "background:rgb(17 24 39/.92);color:#fff;font-size:.875rem;pointer-events:none;transition:opacity .3s";
  document.body.append(el);
  setTimeout(() => {
    el.style.opacity = "0";
    setTimeout(() => el.remove(), 300);
  }, EXIT_WINDOW_MS - 300);
}

/**
 * The phone's back button, the way Android apps behave: close the open dialog first, then go
 * back a page, then (on the first page) ask for a second press before leaving the app.
 */
export function installNativeBack(router: AnyRouter) {
  let lastPress = 0;
  window.zeetaBack = () => {
    const open = [...document.querySelectorAll<HTMLDialogElement>("dialog[open]")].at(-1);
    if (open) {
      // The same as pressing Escape: the dialog's own cancel handler closes it.
      open.dispatchEvent(new Event("cancel", { cancelable: true }));
      return true;
    }
    if (router.history.canGoBack()) {
      router.history.back();
      return true;
    }
    const now = Date.now();
    if (now - lastPress < EXIT_WINDOW_MS) return "exit";
    lastPress = now;
    toast(
      document.documentElement.lang === "en" ? "Press back again to exit" : "বের হতে আবার ব্যাক চাপুন",
    );
    return true;
  };
}
